import { logger } from '@repo/logger';
import { useCallback, useEffect, useState } from 'react';

const KEY = 'invoice-studio.active-organization-id.v1';

export interface ActiveOrgStorage {
  loaded: boolean;
  activeOrganizationId: string | null;
  setActiveOrganizationId: (id: string | null) => void;
}

/**
 * Web counterpart to `apps/mobile/session/activeOrganizationStorage.ts`.
 *
 * Synchronously reads the persisted org id from `localStorage` on
 * mount, then exposes a setter that mirrors writes. The
 * <ActiveOrganizationProvider> from `@repo/api-client/react` accepts
 * the `value`+`onChange` pair and resolves the persisted id against
 * the live org list.
 */
export function useActiveOrgStorage(): ActiveOrgStorage {
  const [loaded, setLoaded] = useState(false);
  const [activeOrganizationId, setActiveOrganizationIdState] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      setLoaded(true);
      return;
    }
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw && raw.length > 0) {
        setActiveOrganizationIdState(raw);
      }
    } catch (err) {
      logger.warn('activeOrgStorage.read failed', { error: err });
    } finally {
      setLoaded(true);
    }
  }, []);

  const setActiveOrganizationId = useCallback((id: string | null) => {
    setActiveOrganizationIdState(id);
    if (typeof window === 'undefined') return;
    try {
      if (id === null) {
        window.localStorage.removeItem(KEY);
      } else {
        window.localStorage.setItem(KEY, id);
      }
    } catch (err) {
      logger.warn('activeOrgStorage.write failed', { error: err });
    }
  }, []);

  return { loaded, activeOrganizationId, setActiveOrganizationId };
}
